import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Switch,
    CircularProgress,
    TextField,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { ChevronLeft, Heart, Plus } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import EntityAvatar from '../EntityAvatar';
import { colors } from '../../styles/themeVars';
import { glassFloatSx, glassFloatHoverSx, hairlineColor, tint } from '../../styles/surfaces';
import { computeTipBreakdown } from '../../utils/tippingFee';
import { buildTipReturnUrl } from '../../utils/tipReturn';
import tipApi, { type TipRecipientType, type CreateTipIntentResponse } from '../../services/tipApi';
import { showToast } from '../../utils/toast';
import { getStripePromise } from './stripeClient';
import TipThanks from './TipThanks';

// Mirrors the backend `config.tipping` defaults. The breakdown shown here is an
// instant ESTIMATE; the server returns the authoritative numbers when the intent
// is created (and the recipient always gets exactly the stated tip when "cover
// fees" is on).
const STRIPE_PCT_BPS = 290;
const STRIPE_FLAT_CENTS = 30;
const LIGHTS_ON_SUGGESTED_CENTS = 150;
const MIN_TIP_CENTS = 100;
const MAX_TIP_CENTS = 50000;
const PRESET_DOLLARS = [2, 5, 10, 20];

const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const stepIn = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }`;

export interface TipSheetProps {
    open: boolean;
    onClose: () => void;
    recipientType: TipRecipientType;
    recipientId: string;
    recipientName: string;
    /** Present for live-room tips so the celebration broadcasts to the room. */
    roomCode?: string | null;
    onSuccess?: () => void;
}

type Phase = 'amount' | 'payment' | 'done';

/**
 * Full-screen, immersive real-money tip flow — a section of the app (back button
 * top-left, app theme, safe-area aware), not a modal. The amount is the hero: a
 * big tap-to-type field with quick-add chips, the two transparent choices (cover
 * fees, keep-the-lights-on) inline, then the embedded Stripe Payment Element, and
 * a short celebratory thank-you beat on success.
 */
export default function TipSheet(props: TipSheetProps): React.ReactElement | null {
    const { open, onClose, recipientType, recipientId, recipientName, roomCode, onSuccess } = props;

    const [amountInput, setAmountInput] = useState<string>('');
    const [coverFees, setCoverFees] = useState<boolean>(true);
    // On by default and labelled optional: it is the only money
    // Showliner keeps, and Connect bills a fixed fee for every act that cashes out.
    const [lightsOn, setLightsOn] = useState<boolean>(true);
    const [message, setMessage] = useState<string>('');
    const [showMessage, setShowMessage] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [intent, setIntent] = useState<CreateTipIntentResponse | null>(null);
    const [phase, setPhase] = useState<Phase>('amount');
    const inputRef = useRef<HTMLInputElement>(null);

    const tipCents = Math.round((parseFloat(amountInput) || 0) * 100);
    const lightsOnCents = lightsOn ? LIGHTS_ON_SUGGESTED_CENTS : 0;
    const tooLow = tipCents > 0 && tipCents < MIN_TIP_CENTS;
    const tooHigh = tipCents > MAX_TIP_CENTS;
    const valid = tipCents >= MIN_TIP_CENTS && tipCents <= MAX_TIP_CENTS;

    // Instant client-side estimate (the server re-computes authoritatively).
    const breakdown = useMemo(
        () => computeTipBreakdown({
            tipCents: valid ? tipCents : 0,
            coverFees,
            lightsOnCents,
            stripePctBps: STRIPE_PCT_BPS,
            stripeFlatCents: STRIPE_FLAT_CENTS,
        }),
        [tipCents, valid, coverFees, lightsOnCents],
    );

    // Open straight into the amount: focus the field so the numeric keypad
    // appears and the cursor sits on the zero (typing replaces it, Venmo-style).
    useEffect(() => {
        if (open && phase === 'amount') {
            const t = setTimeout(() => inputRef.current?.focus(), 60);
            return () => clearTimeout(t);
        }
    }, [open, phase]);

    if (!open) return null;

    const resetAndClose = () => {
        setIntent(null);
        setPhase('amount');
        setSubmitting(false);
        onClose();
    };

    const onAmountChange = (raw: string) => {
        // Digits + a single dot, max two decimals.
        let v = raw.replace(/[^0-9.]/g, '');
        const dot = v.indexOf('.');
        if (dot !== -1) {
            v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
            const [int, dec] = v.split('.');
            v = int.slice(0, 4) + '.' + (dec ?? '').slice(0, 2);
        } else {
            v = v.slice(0, 4);
        }
        setAmountInput(v);
    };

    const pickPreset = (dollars: number) => {
        setAmountInput(String(dollars));
        inputRef.current?.focus();
    };

    const continueToPayment = async () => {
        if (!valid) return;
        setSubmitting(true);
        try {
            const result = await tipApi.createIntent({
                recipientType,
                recipientId,
                tipCents,
                coverFees,
                lightsOnCents,
                roomCode: roomCode || null,
                message: message.trim() || null,
            });
            setIntent(result);
            setPhase('payment');
        } catch (err: any) {
            showToast(err?.response?.data?.error || 'Could not start your tip. Please try again.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const onPaid = () => {
        setPhase('done');
        onSuccess?.();
        // Let the thank-you beat play, then close.
        window.setTimeout(resetAndClose, 1700);
    };

    const back = () => {
        if (phase === 'payment') { setPhase('amount'); return; }
        resetAndClose();
    };

    const presetActive = (d: number) => amountInput === String(d) || amountInput === `${d}.00`;
    const heroAmount = amountInput === '' ? '0' : amountInput;

    // Portal to <body> so the fixed full-screen sheet escapes any ancestor that
    // establishes a containing block (the room's glass panels use backdrop-filter,
    // which would otherwise trap `position: fixed` and clip the sheet to the card).
    const sheet = (
        <Box
            role="dialog"
            aria-modal="true"
            aria-label={`Tip ${recipientName}`}
            sx={(theme) => ({
                position: 'fixed',
                inset: 0,
                zIndex: theme.zIndex.modal,
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            })}
        >
            {/* Stage glow behind the amount — subtle, immersive, non-decorative-as-default. */}
            <Box
                aria-hidden
                sx={(theme) => ({
                    position: 'absolute',
                    top: '-12%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 'min(620px, 130%)',
                    height: '46%',
                    pointerEvents: 'none',
                    background: `radial-gradient(60% 60% at 50% 0%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 70%)`,
                })}
            />

            {/* Top bar: back, top-left — same affordance as the profile panel. */}
            <Box sx={{ position: 'relative', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2.5, px: 4, pt: 'calc(8px + env(safe-area-inset-top))', pb: 2.5 }}>
                <IconButton
                    onClick={back}
                    aria-label="Back"
                    sx={(theme) => ({
                        ...glassFloatSx(theme),
                        color: 'text.primary',
                        '&:hover': glassFloatHoverSx(theme),
                        '&:active': { transform: 'scale(0.95)' },
                    })}
                >
                    <ChevronLeft size={20} strokeWidth={2.4} />
                </IconButton>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary' }}>Tip</Typography>
            </Box>

            {/* Scrolling body */}
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', px: 5 }}>
                {phase === 'done' ? (
                    <DoneView recipientName={recipientName} netCents={breakdown.netToRecipientCents} />
                ) : phase === 'payment' && intent ? (
                    <StepWrap key="payment">
                        <Recipient recipientType={recipientType} recipientName={recipientName} />
                        <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: '0.9rem', mt: 1, mb: 5 }}>
                            Paying <Box component="span" sx={{ color: 'text.primary', fontWeight: 800 }}>{fmt(intent.breakdown.grossCents)}</Box>
                        </Typography>
                        <Elements stripe={getStripePromise()} options={{ clientSecret: intent.clientSecret, appearance: { theme: 'night', variables: { colorPrimary: colors.secondary.main } } }}>
                            <PaymentStep
                                grossCents={intent.breakdown.grossCents}
                                netCents={intent.breakdown.netToRecipientCents}
                                recipientName={recipientName}
                                onPaid={onPaid}
                            />
                        </Elements>
                    </StepWrap>
                ) : (
                    <StepWrap key="amount">
                        <Recipient recipientType={recipientType} recipientName={recipientName} />

                        {/* The amount IS the screen — tap to type, numeric keyboard. */}
                        <Box
                            onClick={() => inputRef.current?.focus()}
                            sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5, cursor: 'text', mt: 4, mb: 1 }}
                        >
                            <Typography sx={{ fontSize: 'clamp(2rem, 9vw, 2.6rem)', fontWeight: 800, color: tipCents > 0 ? 'text.primary' : 'text.disabled', lineHeight: 1 }}>$</Typography>
                            <Box
                                component="input"
                                ref={inputRef}
                                inputMode="decimal"
                                type="text"
                                value={amountInput}
                                onChange={(e) => onAmountChange((e.target as HTMLInputElement).value)}
                                placeholder="0"
                                aria-label="Tip amount in dollars"
                                sx={{
                                    all: 'unset',
                                    boxSizing: 'content-box',
                                    textAlign: 'center',
                                    fontVariantNumeric: 'tabular-nums',
                                    width: `calc(${Math.max(heroAmount.length, 1)}ch + 4px)`,
                                    maxWidth: '6ch',
                                    fontSize: 'clamp(3.4rem, 19vw, 5rem)',
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    letterSpacing: '-0.02em',
                                    color: 'text.primary',
                                    caretColor: (theme) => theme.palette.secondary.main,
                                    '&::placeholder': { color: 'text.disabled', opacity: 1 },
                                }}
                            />
                        </Box>

                        {/* Value reinforcement: what the act actually gets. */}
                        <Typography sx={{ textAlign: 'center', minHeight: 22, fontSize: '0.85rem', fontWeight: 700, color: tooLow || tooHigh ? 'warning.main' : 'secondary.main' }}>
                            {tooLow ? `Minimum tip is ${fmt(MIN_TIP_CENTS)}`
                                : tooHigh ? `Maximum tip is ${fmt(MAX_TIP_CENTS)}`
                                    : valid ? `${recipientName} gets ${fmt(breakdown.netToRecipientCents)}`
                                        : ' '}
                        </Typography>

                        {/* Quick-add chips */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2.5, mt: 4.5 }}>
                            {PRESET_DOLLARS.map((d) => {
                                const active = presetActive(d);
                                return (
                                    <Box
                                        key={d}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => pickPreset(d)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickPreset(d); } }}
                                        sx={(theme) => ({
                                            minWidth: 64,
                                            textAlign: 'center',
                                            px: 5,
                                            py: 2.5,
                                            borderRadius: 999,
                                            fontWeight: 800,
                                            fontSize: '0.95rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            transition: 'transform 0.12s ease, background-color 0.15s ease, border-color 0.15s ease',
                                            border: `1px solid ${active ? alpha(theme.palette.secondary.main, 0.6) : hairlineColor(theme, 'subtle')}`,
                                            bgcolor: active ? alpha(theme.palette.secondary.main, 0.18) : tint(theme, 0.06),
                                            color: active ? theme.palette.secondary.main : theme.palette.text.primary,
                                            '&:hover': { bgcolor: active ? alpha(theme.palette.secondary.main, 0.24) : tint(theme, 0.1) },
                                            '&:active': { transform: 'scale(0.95)' },
                                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                        })}
                                    >
                                        ${d}
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Transparent choices */}
                        <Box sx={{ mt: 7.5, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 420, mx: 'auto', width: '100%' }}>
                            <ChoiceRow
                                checked={coverFees}
                                onChange={setCoverFees}
                                title="Cover the fees"
                                sub={coverFees ? `${recipientName} gets your full tip` : `${recipientName} absorbs the ${fmt(breakdown.estStripeFeeCents)} fee`}
                                addon={coverFees && valid ? `+${fmt(Math.max(breakdown.grossCents - tipCents - lightsOnCents, 0))}` : null}
                            />
                            <ChoiceRow
                                checked={lightsOn}
                                onChange={setLightsOn}
                                title="Keep the lights on"
                                sub="Chip in to support Showliner (optional)"
                                addon={`+${fmt(LIGHTS_ON_SUGGESTED_CENTS)}`}
                            />

                            {showMessage ? (
                                <TextField
                                    autoFocus
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder={`Say something to ${recipientName}…`}
                                    multiline
                                    minRows={2}
                                    size="small"
                                    inputProps={{ maxLength: 280 }}
                                    sx={{ mt: 1 }}
                                />
                            ) : (
                                <Box
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setShowMessage(true)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowMessage(true); } }}
                                    sx={{ alignSelf: 'flex-start', mt: 2.5, ml: 1, display: 'inline-flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', color: 'text.secondary', fontWeight: 700, fontSize: '0.85rem', transition: 'color 0.15s ease', '&:hover': { color: 'text.primary' } }}
                                >
                                    <Plus size={15} strokeWidth={2.6} /> Add a message
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ height: 24 }} />
                    </StepWrap>
                )}
            </Box>

            {/* Sticky CTA (amount step only — payment has its own button) */}
            {phase === 'amount' && (
                <Box sx={{ position: 'relative', flexShrink: 0, px: 5, pt: 4, pb: 'calc(16px + env(safe-area-inset-bottom))', background: (theme) => `linear-gradient(to top, ${theme.palette.background.default} 70%, transparent)` }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        onClick={continueToPayment}
                        disabled={!valid || submitting}
                        startIcon={valid && !submitting ? <Heart size={18} strokeWidth={2.6} /> : undefined}
                        sx={(theme) => ({
                            py: 3.5,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 900,
                            fontSize: '1.02rem',
                            // Before a valid amount the button is disabled — give it a
                            // readable neutral fill instead of the default faint-on-pink.
                            ...(!valid ? {
                                '&.Mui-disabled': {
                                    background: tint(theme, 0.1),
                                    backgroundImage: 'none',
                                    color: 'text.secondary',
                                },
                            } : {}),
                        })}
                    >
                        {submitting ? <CircularProgress size={22} color="inherit" /> : valid ? `Tip ${fmt(breakdown.grossCents)}` : 'Enter an amount'}
                    </Button>
                </Box>
            )}
        </Box>
    );

    return createPortal(sheet, document.body);
}

function StepWrap({ children }: { children: React.ReactNode }): React.ReactElement {
    return (
        <Box
            sx={{
                maxWidth: 460,
                mx: 'auto',
                pt: 2.5,
                animation: `${stepIn} 260ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
        >
            {children}
        </Box>
    );
}

function Recipient({ recipientType, recipientName }: { recipientType: TipRecipientType; recipientName: string }): React.ReactElement {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, textAlign: 'center' }}>
            <EntityAvatar entityType={recipientType === 'venue' ? 'venue' : 'performer'} name={recipientName} size={56} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', overflowWrap: 'anywhere' }}>
                Tip {recipientName}
            </Typography>
        </Box>
    );
}

function ChoiceRow({ checked, onChange, title, sub, addon }: {
    checked: boolean;
    onChange: (next: boolean) => void;
    title: string;
    sub: string;
    addon: string | null;
}): React.ReactElement {
    return (
        <Box
            onClick={() => onChange(!checked)}
            sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                px: 4,
                py: 3,
                borderRadius: '14px',
                cursor: 'pointer',
                border: `1px solid ${checked ? alpha(theme.palette.secondary.main, 0.4) : hairlineColor(theme, 'faint')}`,
                bgcolor: checked ? alpha(theme.palette.secondary.main, 0.1) : tint(theme, 0.05),
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
            })}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.92rem' }}>{title}</Typography>
                    {addon && <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary' }}>{addon}</Typography>}
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.3 }}>{sub}</Typography>
            </Box>
            <Switch
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                color="secondary"
                edge="end"
            />
        </Box>
    );
}

function DoneView({ recipientName, netCents }: { recipientName: string; netCents: number }): React.ReactElement {
    // The card path only ever lands here on success — a redirect method that
    // comes back `processing` finishes on a fresh page load, in the return
    // handler, which renders this same component with the honest outcome.
    return (
        <Box sx={{ pt: '22vh' }}>
            <TipThanks recipientName={recipientName} netCents={netCents} outcome="succeeded" />
        </Box>
    );
}

/** Inner step rendered within <Elements> so the Stripe hooks are available. */
function PaymentStep({ grossCents, netCents, recipientName, onPaid }: { grossCents: number; netCents: number; recipientName: string; onPaid: () => void }): React.ReactElement {
    const stripe = useStripe();
    const elements = useElements();
    const [paying, setPaying] = useState(false);

    const pay = async () => {
        if (!stripe || !elements) return;
        setPaying(true);
        try {
            // `return_url` is REQUIRED even under `redirect: 'if_required'`.
            // The flag only means "don't redirect unless the chosen method
            // demands it" — Stripe still refuses to confirm without somewhere
            // to come back to, because it cannot know what the fan will pick
            // until they pick it. Cards never redirect, so omitting it works
            // perfectly right up until someone taps Cash App Pay, which always
            // does; the whole method then fails with a `return_url` error and
            // no charge. Every redirect-based method (Cash App, Klarna,
            // Affirm, Amazon Pay) was dead this way, and which of them are
            // even offered is a dashboard setting, so the set can grow without
            // a deploy.
            //
            // The fan comes back to the page they are standing on, carrying
            // just enough to be told what happened — see `utils/tipReturn`.
            const returnUrl = buildTipReturnUrl(window.location.href, { recipientName, netCents });
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: returnUrl },
                redirect: 'if_required',
            });
            if (error) {
                showToast(error.message || 'Payment failed. Please try again.', 'error');
            } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
                showToast(`Thank you for tipping ${recipientName}!`, 'success');
                onPaid();
                return;
            }
        } catch (err: any) {
            showToast(err?.message || 'Payment failed. Please try again.', 'error');
        } finally {
            setPaying(false);
        }
    };

    return (
        <Box sx={{ pb: 'calc(24px + env(safe-area-inset-bottom))' }}>
            <PaymentElement />
            <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={pay}
                disabled={!stripe || paying}
                sx={{ mt: 6, fontSize: '1.02rem' }}
            >
                {paying ? <CircularProgress size={22} color="inherit" /> : `Pay ${fmt(grossCents)}`}
            </Button>
        </Box>
    );
}
